import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { IconSvgElement } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  Bug02Icon,
  Call02Icon,
  CallIncoming04Icon,
  CallOutgoing04Icon,
  ChartColumnIcon,
  Clock02Icon,
  DashboardSquare02Icon,
  Database02Icon,
  DatabaseLightningIcon,
  HelpSquareIcon,
  Key01Icon,
  LayoutGridIcon,
  Mortarboard02Icon,
  PackageIcon,
  Settings01Icon,
  SparklesIcon,
  UserMultiple03Icon,
} from "@hugeicons/core-free-icons";

export interface NavItem {
  id: string;
  label: MessageDescriptor;
  path: string;
  icon: IconSvgElement;
}

export interface NavGroup {
  /** Stable identifier used for React keys and `data-testid` (never rendered). */
  id: string;
  name: MessageDescriptor;
  items: NavItem[];
}

export interface NavGroups {
  admin: NavGroup[];
  user: NavGroup[];
}

/**
 * Top-level navigation groups for the application sidebar.
 *
 * Mirrors `voicebot-ui-redesign/project/src/shell.jsx` NAV_GROUPS, but
 * drops `knowledge-qa` (out of scope) and routes points at concrete
 * paths owned by the flat router.
 */
export const NAV_GROUPS: NavGroups = {
  admin: [
    {
      id: "live",
      name: msg`Live`,
      items: [
        {
          id: "dashboard",
          label: msg`Dashboard`,
          path: "/monitoring/dashboard",
          icon: DashboardSquare02Icon,
        },
        {
          id: "one-call",
          label: msg`One Call`,
          path: "/monitoring/one-call",
          icon: CallIncoming04Icon,
        },
        {
          id: "one-to-many",
          label: msg`Multi-Call`,
          path: "/monitoring/one-to-many",
          icon: LayoutGridIcon,
        },
      ],
    },
    {
      id: "configuration",
      name: msg`Configuration`,
      items: [
        {
          id: "config-sets",
          label: msg`Config Sets`,
          path: "/admin/config-sets",
          icon: Settings01Icon,
        },
        {
          id: "faq-sets",
          label: msg`FAQ Sets`,
          path: "/admin/faq-sets",
          icon: HelpSquareIcon,
        },
        {
          id: "transfers",
          label: msg`Transfers`,
          path: "/admin/transfers",
          icon: CallOutgoing04Icon,
        },
        {
          id: "call-groups",
          label: msg`Call Groups`,
          path: "/admin/call-groups",
          icon: UserMultiple03Icon,
        },
        {
          id: "sip-numbers",
          label: msg`SIP Numbers`,
          path: "/admin/sip-numbers",
          icon: Call02Icon,
        },
        {
          id: "avatars-3d",
          label: msg`3D Avatars`,
          path: "/admin/avatars-3d",
          icon: PackageIcon,
        },
        {
          id: "avatars-liveavatar",
          label: msg`Liveavatar`,
          path: "/admin/avatars-liveavatar",
          icon: SparklesIcon,
        },
      ],
    },
    {
      id: "data-management",
      name: msg`Data Management`,
      items: [
        {
          id: "customers",
          label: msg`Customers`,
          path: "/admin/customers",
          icon: Database02Icon,
        },
        {
          id: "call-history",
          label: msg`Call History`,
          path: "/admin/call-history",
          icon: Clock02Icon,
        },
        {
          id: "learnings",
          label: msg`Learnings`,
          path: "/admin/learnings",
          icon: Mortarboard02Icon,
        },
        {
          id: "voice-auth-admin",
          label: msg`Voice Auth`,
          path: "/admin/voice-auth",
          icon: Key01Icon,
        },
        {
          id: "a2a-requests",
          label: msg`A2A Requests`,
          path: "/admin/a2a-requests",
          icon: ArrowDataTransferHorizontalIcon,
        },
        {
          id: "statistics",
          label: msg`Statistics`,
          path: "/admin/statistics",
          icon: ChartColumnIcon,
        },
      ],
    },
    {
      id: "development",
      name: msg`Development`,
      items: [
        {
          id: "debug",
          label: msg`Debug`,
          path: "/admin/debug",
          icon: Bug02Icon,
        },
        {
          id: "database",
          label: msg`Database`,
          path: "/admin/database",
          icon: DatabaseLightningIcon,
        },
      ],
    },
  ],
  user: [
    {
      id: "monitoring",
      name: msg`Monitoring`,
      items: [
        {
          id: "dashboard",
          label: msg`Dashboard`,
          path: "/monitoring/dashboard",
          icon: DashboardSquare02Icon,
        },
        {
          id: "one-call",
          label: msg`One Call`,
          path: "/monitoring/one-call",
          icon: CallIncoming04Icon,
        },
        {
          id: "one-to-many",
          label: msg`Multi-Call`,
          path: "/monitoring/one-to-many",
          icon: LayoutGridIcon,
        },
      ],
    },
  ],
};

/**
 * Routes where the sidebar may collapse. All other routes force expansion.
 */
export const MONITORING_PATHS = [
  "/monitoring/dashboard",
  "/monitoring/one-call",
  "/monitoring/one-to-many",
] as const;
